import { produce } from 'immer';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  hasFormChanged,
  mergeCalendarItems,
  mergeCalendarItemsObject,
  resetCalendarSelection,
  toggleItemSelectionByDate,
  toggleSelectedDate,
} from '@/utils/calendarUtils';

/**
 * Zustand store for managing the Salary state.
 *
 * This store uses Zustand's `devtools` middleware for easy debugging during development.
 */
const initialValues = {
  // Loading of fetched salary data
  isLoading: null,

  isDirty: false,

  isUpdatedLoading: null,

  selectedDates: [],

  // Structured data from fetch
  originalData: {
    data: null,
    workTypes: [],
    hoursByDay: [],
  },

  // Updated data after modifications
  updatedData: {
    data: null,
    workTypes: [],
    hoursByDay: [],
    suspensionDrawer: [],
  },

  // Toggle Modal and Sidebars
  toggle: {
    menu: false,
    submenu: false, // deprecated, use suspensionSubmenu
    suspensionSubmenu: false,
    bonusSubmenu: false,
    nonMonetaryBenefitSubmenu: false,
    action: false,
    terminate: false,
    leaveExtender: false,
    isWeekend: false,
  },
};

export const useSalaryStore = create(
  devtools(
    immer((set, get) => ({
      ...initialValues,

      setOriginalData: ({ isLoading, ...originalData }) => {
        const { data, workTypes, hoursByDay } = originalData;
        const updatedHoursByDay = resetCalendarSelection(hoursByDay);

        const newData = {
          data: {
            ...data,
            uploadedFiles: data?.uploadedFiles || [],
            suspensionDrawer: data?.suspensionDrawer || [],
          },
          workTypes,
          hoursByDay: updatedHoursByDay,
        };

        set({
          isLoading,
          originalData: newData,
          updatedData: newData,
          selectedDates: [],
        });
      },

      setUpdatedOriginalData: ({
        action,
        payload: { currentDate, formValues },
      }) => {
        const { updatedData, selectedDates, originalData } = get();

        switch (action) {
          case 'selected': {
            const updatedHoursByDay = toggleItemSelectionByDate(
              updatedData.hoursByDay,
              currentDate,
              selectedDates
            );

            set(
              produce((state) => {
                state.selectedDates = toggleSelectedDate(
                  state.selectedDates,
                  currentDate
                );
                state.updatedData.hoursByDay = updatedHoursByDay;
              })
            );

            return;
          }
          case 'submitted': {
            const submittedHoursByDay = mergeCalendarItems(
              updatedData.hoursByDay,
              formValues
            );

            set(
              produce((state) => {
                state.isUpdatedLoading = true;
                state.selectedDates = [];
                state.updatedData.hoursByDay = submittedHoursByDay;
                state.isDirty = hasFormChanged(originalData, {
                  ...updatedData,
                  hoursByDay: submittedHoursByDay,
                });
                state.toggle.action = false;
              })
            );

            setTimeout(() => {
              set({ isUpdatedLoading: false });
            }, 1000);

            return;
          }
          case 'menuSubmitted': {
            set(
              produce((state) => {
                Object.keys(formValues).forEach((key) => {
                  if (originalData.data[key] !== undefined) {
                    if (
                      key === 'currencies' &&
                      typeof formValues[key] === 'object'
                    ) {
                      state.updatedData.data[key] = {
                        ...state.updatedData.data[key],
                        ...formValues[key],
                      };
                    } else {
                      state.updatedData.data[key] = formValues[key];
                    }
                  }
                });

                if (formValues.uploadedFiles) {
                  state.updatedData.data.uploadedFiles =
                    formValues?.uploadedFiles || [];
                }

                state.isDirty = hasFormChanged(originalData, {
                  ...updatedData,
                  data: mergeCalendarItemsObject(
                    {
                      ...updatedData.data,
                      uploadedFiles: formValues.uploadedFiles || [],
                      suspensionDrawer: formValues.suspensionDrawer || [],
                    },
                    formValues
                  ),
                });
              })
            );

            return;
          }
          case 'submenuSubmitted': {
            const {
              suspensionDrawer,
              suspensionVariationsDrawer,
              suspensionCurrency,
              generalSuspensionData,
              initialSuspension,
            } = formValues;

            // Sum free entry suspensions + initial (existing attachments)
            let freeEntrySuspension = 0;
            suspensionDrawer.forEach((item) => {
              freeEntrySuspension += parseFloat(item.valueSuspension) || 0;
            });
            const suspensionAmount =
              freeEntrySuspension + (initialSuspension || 0);

            set(
              produce((state) => {
                state.updatedData.data.suspensionDrawer = suspensionDrawer;
                state.updatedData.data.suspensionVariationsDrawer =
                  suspensionVariationsDrawer || [];
                state.updatedData.data.suspension = suspensionAmount;
                state.updatedData.data.generalSuspensionData =
                  generalSuspensionData;
                if (suspensionCurrency) {
                  state.updatedData.data.suspensionCurrency =
                    suspensionCurrency;
                  if (state.updatedData.data.currencies) {
                    state.updatedData.data.currencies.suspensionCurrency =
                      suspensionCurrency;
                  }
                }

                state.isDirty = hasFormChanged(originalData, {
                  ...updatedData,
                  data: {
                    ...updatedData.data,
                    suspensionDrawer,
                    suspensionVariationsDrawer:
                      suspensionVariationsDrawer || [],
                    suspension: suspensionAmount,
                    generalSuspensionData,
                  },
                });

                state.toggle.suspensionSubmenu = false;
              })
            );
            return;
          }
          case 'bonusSubmenuSubmitted': {
            const {
              bonusDrawer,
              bonus,
              totalBonus,
              generalBonusData,
              bonusCurrency,
            } = formValues;

            set(
              produce((state) => {
                state.updatedData.data.bonusDrawer = bonusDrawer;
                state.updatedData.data.bonus = bonus;
                state.updatedData.data.totalBonus = totalBonus;
                state.updatedData.data.generalBonusData = generalBonusData;
                if (bonusCurrency) {
                  state.updatedData.data.bonusCurrency = bonusCurrency;
                  if (state.updatedData.data.currencies) {
                    state.updatedData.data.currencies.bonusCurrency =
                      bonusCurrency;
                  }
                }

                state.isDirty = hasFormChanged(originalData, {
                  ...updatedData,
                  data: {
                    ...updatedData.data,
                    bonusDrawer,
                    bonus,
                    totalBonus,
                    generalBonusData,
                  },
                });

                state.toggle.bonusSubmenu = false;
              })
            );
            return;
          }
          case 'nonMonetaryBenefitSubmenuSubmitted': {
            const {
              nonMonetaryBenefitDrawer,
              nonMonetaryBenefit,
              totalNonMonetaryBenefit,
              generalNonMonetaryBenefitData,
              nonMonetaryBenefitCurrency,
            } = formValues;

            set(
              produce((state) => {
                state.updatedData.data.nonMonetaryBenefitDrawer =
                  nonMonetaryBenefitDrawer;
                state.updatedData.data.nonMonetaryBenefit = nonMonetaryBenefit;
                state.updatedData.data.totalNonMonetaryBenefit =
                  totalNonMonetaryBenefit;
                state.updatedData.data.generalNonMonetaryBenefitData =
                  generalNonMonetaryBenefitData;
                if (nonMonetaryBenefitCurrency) {
                  state.updatedData.data.nonMonetaryBenefitCurrency =
                    nonMonetaryBenefitCurrency;
                  if (state.updatedData.data.currencies) {
                    state.updatedData.data.currencies.nonMonetaryBenefitCurrency =
                      nonMonetaryBenefitCurrency;
                  }
                }

                state.isDirty = hasFormChanged(originalData, {
                  ...updatedData,
                  data: {
                    ...updatedData.data,
                    nonMonetaryBenefitDrawer,
                    nonMonetaryBenefit,
                    totalNonMonetaryBenefit,
                    generalNonMonetaryBenefitData,
                  },
                });

                state.toggle.nonMonetaryBenefitSubmenu = false;
              })
            );
            return;
          }
        }
      },

      setToggle: (key, value) => {
        set(
          produce((state) => {
            state.toggle[key] = value;
          })
        );
      },

      reset: () => {
        set({
          isDirty: false,
          selectedDates: [],
          updatedData: { ...get().originalData },
        });
      },
      setIsDirty: () => {
        set(
          produce((state) => {
            state.isDirty = true;
          })
        );
      },
    })),
    {
      name: 'SalaryStore',
      trace: true,
    }
  )
);
